"use client";

import React from "react";
import {
  Box,
  Typography,
  AvatarGroup,
  useTheme,
  alpha,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ImageAvatar } from "components/Circle/shared/ImageAvatar";

interface Member {
  User: {
    id: string;
    firstName: string;
    lastName: string;
    displayImg: string;
    email: string;
  };
  status: string;
}

interface PendingMembersCardProps {
  startDate: string;
  members: Member[];
  setOpen: (open: boolean) => void;
  adminId?: string;
}

const PendingMembersCard = ({
  members,
  setOpen,
}: PendingMembersCardProps) => {
  const theme = useTheme();
  const pendingCount = members.filter((m) => m.status === "pending").length;
  const approvedCount = members.filter((m) => m.status === "approved" || m.status === "accepted").length;

  return (
    <Box
      onClick={() => setOpen(true)}
      sx={{
        flex: 1,
        minWidth: 220,
        maxWidth: 340,
        borderRadius: 3,
        border: "1px solid",
        borderColor: pendingCount > 0
          ? alpha(theme.palette.warning.main, 0.3)
          : "divider",
        bgcolor: pendingCount > 0
          ? alpha(theme.palette.warning.main, 0.03)
          : "background.default",
        p: 2.5,
        cursor: "pointer",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <GroupIcon sx={{ fontSize: "1.1rem", color: "warning.main" }} />
            {pendingCount > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: "warning.main",
                  border: "2px solid",
                  borderColor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "white", lineHeight: 1 }}>
                  {pendingCount}
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "text.primary" }}>
              Members
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              {members.length} total · {pendingCount} pending
            </Typography>
          </Box>
        </Box>
        <ChevronRightIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />
      </Box>

      <AvatarGroup
        max={4}
        sx={{
          justifyContent: "flex-start",
          "& .MuiAvatar-root": {
            width: 32,
            height: 32,
            fontSize: "0.75rem",
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
          },
        }}
      >
        {members.map((member) => (
          <ImageAvatar
            key={member.User.id}
            displayImg={member.User.displayImg || ""}
            firstName={member.User.firstName}
          />
        ))}
      </AvatarGroup>

      {pendingCount > 0 && (
        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "warning.dark" }}>
            {pendingCount} awaiting decision
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PendingMembersCard;
